/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, LocateFixed, Map, ChevronRight, Plus, MapPin, Trash2, Edit, Home, Building, Store, MapPin as MapPinIcon } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import addressService, { AddressWithId } from '../services/addressService';
import { Address as AddressType } from '../types/api';
import { toast } from 'react-hot-toast';
import MapLocationModal from '../components/map/MapLocationModal';
import { authService } from '../services';
import { User } from '../types/api';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import type { GoogleMapsGeocoderResult } from '../types/google-maps';
import PageHeader from '../components/ui/PageHeader';
import { useTranslation } from 'react-i18next';

// Use the Address type from the API types
type Address = AddressType & {
  id?: string;
  isDefault?: boolean;
  contactName?: string;
  contactPhone?: string;
};

// Function to get address label icon and styling
const getAddressLabelDisplay = (label?: string) => {
  switch (label?.toUpperCase()) {
    case 'HOME':
      return {
        icon: Home,
        bgColor: 'bg-green-100',
        textColor: 'text-green-600',
        borderColor: 'border-green-200',
        text: 'Home'
      };
    case 'OFFICE':
      return {
        icon: Building,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-600',
        borderColor: 'border-blue-200',
        text: 'Office'
      };
    case 'SHOP':
      return {
        icon: Store,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-600',
        borderColor: 'border-purple-200',
        text: 'Shop'
      };
    case 'OTHER':
      return {
        icon: MapPinIcon,
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-600',
        borderColor: 'border-orange-200',
        text: 'Other'
      };
    default:
      return {
        icon: MapPinIcon,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-200',
        text: 'Location'
      };
  }
};

// Function to load Google Maps API
const loadGoogleMapsAPI = () => {
  return new Promise<void>((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => {
      console.error('Google Maps API failed to load:', e);
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  });
};

export default function Address() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const returnTo = location.state?.returnTo || '/home';
  const selectedAddress = location.state?.selectedAddress;
  const onAddressUpdate = location.state?.onAddressUpdate;
  const isSubscription = location.state?.isSubscription || false;
  const newAddress = location.state?.newAddress;
  const navigationIntent = location.state?.navigationIntent || '';
  
  // Ref to track if addresses have been loaded from API
  const addressesLoaded = useRef(false);
  
  // State for addresses
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for modal controls
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [editAddressData, setEditAddressData] = useState<Address | null>(null);
  
  // State for current location
  const [currentLocation, setCurrentLocation] = useState<{
    address: string;
    coords?: { lat: number; lng: number };
    loading: boolean;
    error?: string;
  }>({
    address: 'Fetching location...',
    loading: false,
    error: undefined
  });
  
  // State for current user data
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // Load Google Maps API on component mount
  useEffect(() => {
    loadGoogleMapsAPI().catch(err => {
      console.error('Error loading Google Maps API:', err);
      setCurrentLocation(prev => ({
        ...prev,
        error: 'Failed to load Google Maps service'
      }));
    });
  }, []);
  
  // Function to get current user data
  const fetchCurrentUser = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getCurrentUser();
        setCurrentUser(userData);
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };
  
  // Function to get current location using Geolocation API
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setCurrentLocation(prev => ({
        ...prev,
        error: 'Geolocation is not supported by your browser'
      }));
      return;
    }
    
    setCurrentLocation(prev => ({ ...prev, loading: true }));
    
    try {
      // First ensure Google Maps API is loaded
      await loadGoogleMapsAPI();
      
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: coords }, (results: GoogleMapsGeocoderResult[] | null, status: string) => {
        if (status === "OK" && results && results[0]) {
          setCurrentLocation({
            address: results[0].formatted_address,
            coords,
            loading: false
          });
        } else {
          setCurrentLocation({
            address: 'Location found but address details unavailable',
            coords,
            loading: false,
            error: 'Could not get address details for this location'
          });
        }
      });
    } catch (err) {
      console.error('Error getting current location:', err);
      let errorMessage = 'Unable to retrieve your location';
      if (err instanceof GeolocationPositionError) {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location services.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case err.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
      }
      setCurrentLocation({
        address: 'Location access denied',
        loading: false,
        error: errorMessage
      });
    }
  };
  
  // Fetch addresses from API
  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const apiAddresses = await addressService.getAddresses();
      setAddresses(apiAddresses);
      addressesLoaded.current = true; // Mark addresses as loaded
    } catch (err) {
      console.error('Error fetching addresses:', err);
      setError(t('map.addressManagement.errors.failedToLoad'));
      setAddresses([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check if address is a duplicate
  const isDuplicateAddress = (address: Address, addressList: Address[]) => {
    return addressList.some(addr => 
      // Check for duplicates by comparing multiple fields
      addr.street?.toLowerCase() === address.street?.toLowerCase() && 
      addr.city?.toLowerCase() === address.city?.toLowerCase() &&
      addr.zipCode === address.zipCode
    );
  };
  
  // Clean up any existing duplicates in localStorage
  const cleanupDuplicateAddresses = () => {
    try {
      const savedAddresses = JSON.parse(localStorage.getItem('savedAddresses') || '[]');
      if (savedAddresses.length > 0) {
        // Clear out any locally saved addresses - we'll only use server-provided ones
        localStorage.removeItem('savedAddresses');
      }
    } catch (err) {
      console.error('Error cleaning up duplicate addresses:', err);
    }
  };
  
  // Load addresses and get current location on component mount
  useEffect(() => {
    cleanupDuplicateAddresses();
    fetchAddresses();
    getCurrentLocation();
    fetchCurrentUser();
  }, []);
  
  // If there's a new address in the navigation state, add it after addresses are loaded
  useEffect(() => {
    if (newAddress && addressesLoaded.current) {
      // Use function form to avoid stale addresses state
      setAddresses(currentAddresses => {
        // Check if address already exists to prevent duplicates
        if (!isDuplicateAddress(newAddress, currentAddresses)) {
          // Add to API
          const saveAddressToApi = async () => {
            try {
              const createdAddress = await addressService.createAddress(newAddress);
              if (createdAddress) {
                // Update addresses state with the newly created address
                setAddresses(prev => {
                  // Double-check to prevent race conditions
                  if (!isDuplicateAddress(createdAddress, prev)) {
                    return [...prev, createdAddress];
                  }
                  return prev;
                });
              } else {
                toast.error(t('map.addressManagement.errors.failedToSave'));
              }
            } catch (err) {
              console.error('Error saving address to API:', err);
              toast.error(t('map.addressManagement.errors.failedToSave'));
            }
          };
          
          saveAddressToApi();
          
          // Return existing addresses, since the API call will update state later
          return currentAddresses;
        } else {
          return currentAddresses;
        }
      });
    }
  }, [newAddress, addressesLoaded.current]); // Also depend on addressesLoaded

  // When navigating to map location
  const handleAddNewAddress = () => {
    setEditAddressData(null); // Ensure we're not in edit mode
    setIsMapModalOpen(true);
  };

  // Back button handler
  const handleBack = () => {
    navigate(-1);
  };

  // Handle editing an existing address
  const handleEditAddress = (address: Address) => {
    setEditAddressData(address);
    setIsMapModalOpen(true);
  };

  // Handle selecting current location
  const handleCurrentLocationSelect = () => {
    if (currentLocation.coords) {
      setEditAddressData(null); // Ensure we're not in edit mode
      setIsMapModalOpen(true);
    } else {
      // If no coordinates available, try to get them
      getCurrentLocation();
      toast.error(t('map.addressManagement.errors.unableToGetLocation'));
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsMapModalOpen(false);
    setEditAddressData(null);
  };

  // Handle location selection from modal
  const handleLocationSelect = async (locationData: {
    address: string;
    locationName: string;
    latitude: number;
    longitude: number;
  }) => {
    setIsMapModalOpen(false);
    
    // Refresh the address list
    await fetchAddresses();
  };

  const handleDeleteAddress = async (address: Address, index: number) => {
    if (!address.id) {
      // If no ID, just remove from state
      setAddresses(prev => prev.filter((_, i) => i !== index));
      return;
    }
    
    try {
      const success = await addressService.deleteAddress(address.id);
      if (success) {
        setAddresses(prev => prev.filter(addr => addr.id !== address.id));
        toast.success(t('map.addressManagement.success.addressDeleted'));
      } else {
        toast.error(t('map.addressManagement.errors.failedToDelete'));
      }
    } catch (err) {
      console.error('Error deleting address:', err);
      toast.error(t('map.addressManagement.errors.failedToDelete'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader title={t('map.addressManagement.title')} />

      {/* Search Bar */}
      <div className="p-4 border-b">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('map.addressManagement.searchPlaceholder')}
            className="w-full bg-gray-100 py-3 pl-10 pr-4 rounded-full focus:outline-none"
          />
        </div>
      </div>

      {/* Current Location */}
      <div 
        className="p-4 border-b flex items-center justify-between cursor-pointer"
        onClick={handleCurrentLocationSelect}
      >
        <div className="flex items-center">
          <div className="bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center mr-4">
            <LocateFixed size={20} className={currentLocation.loading ? "text-blue-500 animate-pulse" : "text-gray-500"} />
          </div>
          <div>
            <h2 className="text-base font-bold">{t('map.addressManagement.currentLocation')}</h2>
            <p className="text-gray-500 text-sm">
              {currentLocation.loading ? t('map.addressManagement.gettingLocation') : 
               currentLocation.error ? (
                 <span className="text-red-500">{currentLocation.error}</span>
               ) : 
               currentLocation.address}
            </p>
          </div>
        </div>
        <button 
          className="focus:outline-none border-none bg-transparent"
        >
          <ChevronRight size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Choose on Map */}
      <div 
        className="p-4 border-b flex items-center justify-between cursor-pointer"
        onClick={handleAddNewAddress}
      >
        <div className="flex items-center">
          <div className="bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center mr-4">
            <Map size={20} className="text-gray-500" />
          </div>
          <div>
            <h2 className="text-base font-bold">{t('map.addressManagement.chooseOnMap')}</h2>
          </div>
        </div>
        <button className="focus:outline-none border-none bg-transparent">
          <ChevronRight size={20} className="text-gray-500" />
        </button>
      </div>
      
      {/* Main Content */}
      <div className="p-4">
        {/* Add New Address Button */}
        <button
          onClick={handleAddNewAddress}
          className="w-full bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center text-gray-600 hover:border-gray-400 transition-colors mb-6"
        >
          <Plus size={20} className="mr-2" />
          <span>{t('map.addressManagement.addNewAddress')}</span>
        </button>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('map.addressManagement.loadingAddresses')}</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
            {error}
            <button 
              onClick={fetchAddresses} 
              className="ml-2 underline"
            >
              {t('map.addressManagement.retry')}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && addresses.length === 0 && (
          <div className="text-center py-8">
            <div className="mb-4">
              <MapPin size={40} className="text-gray-400 mx-auto" />
            </div>
            <p className="text-gray-600 font-medium mb-2">{t('map.addressManagement.noSavedAddresses')}</p>
            <p className="text-gray-500 text-sm mb-4">{t('map.addressManagement.addNewAddressToContinue')}</p>
            <button 
              onClick={handleAddNewAddress}
              className="mt-2 bg-[#F9D348] text-black font-semibold py-3 px-6 rounded-full"
            >
              {t('map.addressManagement.addNewAddress')}
            </button>
          </div>
        )}

        {/* Address List */}
        {!isLoading && (
          <div className="space-y-4">
            {addresses.map((address, index) => {
              const labelDisplay = getAddressLabelDisplay(address.addressLabel);
              const IconComponent = labelDisplay.icon;
              
              return (
                <div
                  key={address.id || index}
                  className="bg-white rounded-xl p-4 border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <div className={`p-2 rounded-full mr-3 ${labelDisplay.bgColor} ${labelDisplay.borderColor} border`}>
                        <IconComponent size={20} className={labelDisplay.textColor} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{address.locationName || address.name || address.street}</p>
                          {address.addressLabel && (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${labelDisplay.bgColor} ${labelDisplay.textColor} ${labelDisplay.borderColor}`}>
                              <IconComponent size={12} />
                              {labelDisplay.text}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mt-1">
                          {address.city}, {address.state} {address.zipCode}
                        </p>
                        <p className="text-sm text-gray-500">{address.country}</p>
                        {address.contactName && address.contactPhone && (
                          <p className="text-xs text-gray-500 mt-1">
                            {address.contactName} • {address.contactPhone}
                          </p>
                        )}
                        {address.isDefault && (
                          <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mt-1">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditAddress(address)}
                        className="text-gray-400 hover:text-blue-500"
                      >
                        <Edit size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteAddress(address, index)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Map Location Modal */}
      {isMapModalOpen && (
        <MapLocationModal
          isOpen={isMapModalOpen}
          onClose={handleModalClose}
          onSelectLocation={handleLocationSelect}
          initialCoords={currentLocation.coords}
          editMode={!!editAddressData}
          addressToEdit={editAddressData}
          userPhone={currentUser?.phone}
        />
      )}
    </div>
  );
} 